// Journey Content Catalog
// Concrete projects, problems, and challenges available for journey paths.
// Screen names must match the database `screen_name` column exactly.

import { JourneyNodeDifficulty, JourneyNodeType } from './journeyPathConfig';

export interface CatalogItem {
  screenName: string;
  title: string;
  difficulty: JourneyNodeDifficulty;
}

export interface ChallengeCatalogItem {
  screenName: string;
  title: string;
  dayNumber: number;
}

// ─── Projects ────────────────────────────────────────────────────────────────

export const PROJECT_CATALOG: Record<string, CatalogItem> = {
  // Junior (easy)
  todoApp: { screenName: 'todo-app', title: 'Todo App', difficulty: 'easy' },
  musicPlayer: { screenName: 'music-player', title: 'Music Player', difficulty: 'easy' },
  ticTacToe: { screenName: 'tic-tac-toe', title: 'Tic Tac Toe', difficulty: 'easy' },
  businessCard: { screenName: 'business-card', title: 'Business Card', difficulty: 'easy' },
  characterGenerator: { screenName: 'character-generator', title: 'Character Generator', difficulty: 'easy' },
  aiPostGenerator: { screenName: 'ai-post-generator', title: 'AI Post Generator', difficulty: 'easy' },
  colorMemory: { screenName: 'color-memory', title: 'Color Memory', difficulty: 'easy' },
  veggieKing: { screenName: 'the-veggie-king', title: 'The Veggie King', difficulty: 'easy' },
  // Regular (medium)
  pokedex: { screenName: 'pokedex', title: 'Pokedex', difficulty: 'medium' },
  codeEditor: { screenName: 'code-editor', title: 'Code Editor', difficulty: 'medium' },
  quizApp: { screenName: 'quiz-app', title: 'Quiz App', difficulty: 'medium' },
  blog: { screenName: 'blog', title: 'Blog', difficulty: 'medium' },
  dashboard: { screenName: 'dashboard', title: 'Dashboard', difficulty: 'medium' },
  justWeather: { screenName: 'just-weather', title: 'Just Weather', difficulty: 'medium' },
  levelEditor: { screenName: 'level-editor', title: 'Level Editor', difficulty: 'medium' },
  calculator: { screenName: 'calculator', title: 'Calculator', difficulty: 'medium' },
  notely: { screenName: 'notely', title: 'Notely', difficulty: 'medium' },
  gifTok: { screenName: 'giftok', title: 'GifTok', difficulty: 'medium' },
  // Senior (hard)
  findMovies: { screenName: 'find-movies', title: 'Find Movies', difficulty: 'hard' },
  foodieGo: { screenName: 'foodiego', title: 'FoodieGo', difficulty: 'hard' },
  anyChat: { screenName: 'any-chat', title: 'Any Chat', difficulty: 'hard' },
};

// ─── Practice Problems ───────────────────────────────────────────────────────

export const PROBLEM_CATALOG: Record<string, CatalogItem> = {
  // Junior (easy)
  palindrome: { screenName: 'palindrome', title: 'Palindrome', difficulty: 'easy' },
  sortArray: { screenName: 'sort-array', title: 'Sort Array', difficulty: 'easy' },
  twoSum: { screenName: 'two-sum', title: 'Two Sum', difficulty: 'easy' },
  findMax: { screenName: 'find-max', title: 'Find Max', difficulty: 'easy' },
  reverseString: { screenName: 'reverse-string', title: 'Reverse String', difficulty: 'easy' },
  sumArray: { screenName: 'sum-array', title: 'Sum Array', difficulty: 'easy' },
  multiply: { screenName: 'multiply', title: 'Multiply', difficulty: 'easy' },
  countVowels: { screenName: 'count-vowels', title: 'Count Vowels', difficulty: 'easy' },
  isEven: { screenName: 'is-even', title: 'Is Even', difficulty: 'easy' },
  factorial: { screenName: 'factorial', title: 'Factorial', difficulty: 'easy' },
  findMin: { screenName: 'find-min', title: 'Find Min', difficulty: 'easy' },
  // Regular (medium)
  mergeIntervals: { screenName: 'merge-intervals', title: 'Merge Intervals', difficulty: 'medium' },
  groupAnagrams: { screenName: 'group-anagrams', title: 'Group Anagrams', difficulty: 'medium' },
  mergeSortedLists: { screenName: 'merge-sorted-lists', title: 'Merge Sorted Lists', difficulty: 'medium' },
  threeSum: { screenName: 'three-sum', title: 'Three Sum', difficulty: 'medium' },
  productExceptSelf: { screenName: 'product-except-self', title: 'Product Except Self', difficulty: 'medium' },
  maxSubarray: { screenName: 'max-subarray', title: 'Max Subarray', difficulty: 'medium' },
  findPeakElement: { screenName: 'find-peak-element', title: 'Find Peak Element', difficulty: 'medium' },
  firstMissingPositive: { screenName: 'first-missing-positive', title: 'First Missing Positive', difficulty: 'medium' },
  containerMostWater: { screenName: 'container-most-water', title: 'Container Most Water', difficulty: 'medium' },
  bestTimeBuySell: { screenName: 'best-time-buy-sell-stock', title: 'Best Time Buy Sell', difficulty: 'medium' },
  longestConsecutive: { screenName: 'longest-consecutive', title: 'Longest Consecutive', difficulty: 'medium' },
  // Senior (hard)
  topNFrequentWords: { screenName: 'top-n-frequent-words', title: 'Top N Frequent Words', difficulty: 'hard' },
  searchRotatedArray: { screenName: 'search-rotated-array', title: 'Search Rotated Array', difficulty: 'hard' },
  topKFrequent: { screenName: 'top-k-frequent-elements', title: 'Top K Frequent', difficulty: 'hard' },
  binaryTreeLevel: { screenName: 'binary-tree-level-order-traversal', title: 'Binary Tree Level Order', difficulty: 'hard' },
  subsets: { screenName: 'subsets', title: 'Subsets', difficulty: 'hard' },
  wordBreak: { screenName: 'word-break', title: 'Word Break', difficulty: 'hard' },
  permutations: { screenName: 'permutations', title: 'Permutations', difficulty: 'hard' },
  longestPalindromic: { screenName: 'longest-palindromic-substring', title: 'Longest Palindromic', difficulty: 'hard' },
};

// ─── Challenges (first 40 days) ──────────────────────────────────────────────

export const CHALLENGE_CATALOG: Record<string, ChallengeCatalogItem> = {
  profileCard: { screenName: 'profile-card', title: 'Profile Card', dayNumber: 1 },
  addToBag: { screenName: 'add-to-bag', title: 'Add To Bag', dayNumber: 2 },
  mobileNavigation: { screenName: 'mobile-navigation', title: 'Mobile Navigation', dayNumber: 3 },
  contactUs: { screenName: 'contact-us', title: 'Contact Us', dayNumber: 4 },
  recipe: { screenName: 'recipe', title: 'Recipe', dayNumber: 5 },
  imageCarousel: { screenName: 'image-carousel', title: 'Image Carousel', dayNumber: 6 },
  createAccount: { screenName: 'create-account', title: 'Create Account', dayNumber: 7 },
  musicEvents: { screenName: 'music-events', title: 'Music Events', dayNumber: 8 },
  passwordGenerator: { screenName: 'password-generator', title: 'Password Generator', dayNumber: 9 },
  signUpPage: { screenName: 'sign-up-page', title: 'Sign Up Page', dayNumber: 10 },
  hotelBooking: { screenName: 'hotel-booking', title: 'Hotel Booking', dayNumber: 11 },
  restaurantReservation: { screenName: 'restaurant-reservation', title: 'Restaurant Reservation', dayNumber: 12 },
  taskBoard: { screenName: 'task-board', title: 'Task Board', dayNumber: 13 },
  shoppingList: { screenName: 'shopping-list', title: 'Shopping List', dayNumber: 14 },
  notifications: { screenName: 'notifications', title: 'Notifications', dayNumber: 15 },
  furFriends: { screenName: 'fur-friends', title: 'Fur Friends', dayNumber: 16 },
  articleSlider: { screenName: 'article-slider', title: 'Article Slider', dayNumber: 17 },
  imagesPreview: { screenName: 'images-preview', title: 'Images Preview', dayNumber: 18 },
  uploadImages: { screenName: 'upload-images', title: 'Upload Images', dayNumber: 19 },
  cardWallet: { screenName: 'card-wallet', title: 'Card Wallet', dayNumber: 20 },
  pricingPlans: { screenName: 'pricing-plans', title: 'Pricing Plans', dayNumber: 21 },
  messages: { screenName: 'messages', title: 'Messages', dayNumber: 22 },
  homePage: { screenName: 'home-page', title: 'Home Page', dayNumber: 23 },
  movieTicket: { screenName: 'movie-ticket', title: 'Movie Ticket', dayNumber: 24 },
  meetingSchedule: { screenName: 'meeting-schedule', title: 'Meeting Schedule', dayNumber: 25 },
  jobBoard: { screenName: 'job-board', title: 'Job Board', dayNumber: 26 },
  leaderboards: { screenName: 'leaderboards', title: 'Leaderboards', dayNumber: 27 },
  playlist: { screenName: 'playlist', title: 'Playlist', dayNumber: 28 },
  videoPlayer: { screenName: 'video-player', title: 'Video Player', dayNumber: 29 },
  invoices: { screenName: 'invoices', title: 'Invoices', dayNumber: 30 },
  dashboardChallenge: { screenName: 'dashboard', title: 'Dashboard', dayNumber: 31 },
  newsletter: { screenName: 'newsletter', title: 'Newsletter', dayNumber: 32 },
  brandVisualizer: { screenName: 'brand-visualizer', title: 'Brand Visualizer', dayNumber: 33 },
  userProfile: { screenName: 'user-profile', title: 'User Profile', dayNumber: 34 },
  rateUs: { screenName: 'rate-us', title: 'Rate Us', dayNumber: 35 },
  sleepApp: { screenName: 'sleep-app', title: 'Sleep App', dayNumber: 36 },
  exploreFlights: { screenName: 'explore-flights', title: 'Explore Flights', dayNumber: 37 },
  musicFestival: { screenName: 'music-festival', title: 'Music Festival', dayNumber: 38 },
  qrCodeScanner: { screenName: 'qr-code-scanner', title: 'QR Code Scanner', dayNumber: 39 },
  faq: { screenName: 'faq', title: 'FAQ', dayNumber: 40 },
};

// ─── All valid screen names (for test validation) ────────────────────────────

export const ALL_PROJECT_SCREEN_NAMES = new Set(
  Object.values(PROJECT_CATALOG).map((p) => p.screenName)
);

export const ALL_PROBLEM_SCREEN_NAMES = new Set(
  Object.values(PROBLEM_CATALOG).map((p) => p.screenName)
);

export const ALL_CHALLENGE_SCREEN_NAMES = new Set(
  Object.values(CHALLENGE_CATALOG).map((c) => c.screenName)
);

/**
 * Validate that a screen_name exists in the appropriate catalog for the given type.
 */
export const isValidScreenName = (type: JourneyNodeType, screenName: string): boolean => {
  switch (type) {
    case 'project':
      return ALL_PROJECT_SCREEN_NAMES.has(screenName);
    case 'problem':
      return ALL_PROBLEM_SCREEN_NAMES.has(screenName);
    case 'challenge':
      return ALL_CHALLENGE_SCREEN_NAMES.has(screenName);
    default:
      return false;
  }
};
